import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo} from 'react';

import {PolymerSubtype} from 'constants/MolbioConstants';
import {Section} from 'constants/NavConstants';
import staticURL from 'lib/staticURL';
import {PolymerSearchView, PolymerSearchViewProps} from 'molbio/BioentitySearchView';
import {clearPolymerSearchViewSearchParams} from 'molbio/BioentitySearchView';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  LINK_TYPE_TO_CONTAINER_POLYMER_SUBTYPE,
  ReplaceableBiointelligentLinkType,
} from 'molbio/biointelligent-links/replace-link/constants';
import PaneWithSidebar from 'molbio/biointelligent-links/replace-link/PaneWithSidebar';
import {FilterKey, Operator} from 'search/SearchConstants';
import {FONT_PROXIMA_14} from 'styles/FontConstants';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import {assertNever} from 'util/Types';

type PolymerSpecificSearchParams = Pick<PolymerSearchViewProps, 'polymerSubtypes' | 'initialFilters'>;

const LINK_TYPE_TO_FILTER_KEY = {
  [BiointelligentLinkType.DNA_PART]: FilterKey.PART_SEQUENCE,
  [BiointelligentLinkType.RNA_PART]: FilterKey.RNA_PART_SEQUENCE,
  [BiointelligentLinkType.TRANSCRIPTION]: FilterKey.TRANSCRIPTIONS,
} as const;

function getSearchParamsForComponentAndLink(
  linkType: ReplaceableBiointelligentLinkType,
  componentPolymerId: string
): PolymerSpecificSearchParams {
  switch (linkType) {
    case BiointelligentLinkType.TRANSCRIPTION:
    case BiointelligentLinkType.DNA_PART:
    case BiointelligentLinkType.RNA_PART: {
      return {
        polymerSubtypes: [LINK_TYPE_TO_CONTAINER_POLYMER_SUBTYPE[linkType]],
        initialFilters: [
          {
            key: LINK_TYPE_TO_FILTER_KEY[linkType],
            value: [componentPolymerId],
            operator: Operator.HAS_ONE_OF,
            locked: true,
          },
        ],
      };
    }
    case BiointelligentLinkType.AMINO_ACID_PART: {
      return {
        polymerSubtypes: [PolymerSubtype.aa_sequence],
        // TODO: add AA Part filter once it exists
        initialFilters: [],
      };
    }
    default: {
      throw new Error(`Unexpected link type: ${assertNever(linkType)}`);
    }
  }
}

interface ContainerSearchPaneProps {
  existingLinkPolymerId: string;
  newLinkPolymerId: string;
  linkType: ReplaceableBiointelligentLinkType;
  selectedIds: Array<string>;
  setSelectedIds: (selectedIds: Array<string>) => void;
  sidebarExpanded: boolean;
  onToggleSidebarExpanded: () => void;
}

export default function ContainerSearchPane(props: ContainerSearchPaneProps): JSX.Element {
  // Memoize so that the object remains stable and a useEffect can be applied when it changes to
  // reset the search params.
  const searchParams = useMemo(
    () => getSearchParamsForComponentAndLink(props.linkType, props.existingLinkPolymerId),
    [props.linkType, props.existingLinkPolymerId]
  );

  useEffect(() => {
    if (searchParams != null) {
      clearPolymerSearchViewSearchParams(
        searchParams.polymerSubtypes,
        Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE,
        searchParams.initialFilters
      );
    }
  }, [searchParams]);

  return (
    <PaneWithSidebar
      sidebarExpanded={props.sidebarExpanded}
      onToggleSidebarExpanded={props.onToggleSidebarExpanded}
      sidebarContent={
        <>
          <p className={css(styles.helpText)}>
            Select sequences that contain the current link to replace with the updated link
          </p>
          <img src={staticURL('/static/img/biointelligent_link_replace_diagram.svg')} />

          <div className={css(styles.diagram)}>
            <svg className={css(styles.arrow)}>
              <path
                d="M13 76.2698L8 73.383V79.1565L13 76.2698ZM13 0.769775H9V1.76978H13V0.769775ZM0.5 9.26978V68.2698H1.5V9.26978H0.5ZM0.5 68.2698C0.5 72.9642 4.30558 76.7698 9 76.7698V75.7698C4.85786 75.7698 1.5 72.4119 1.5 68.2698H0.5ZM9 0.769775C4.30558 0.769775 0.5 4.57536 0.5 9.26978H1.5C1.5 5.12764 4.85786 1.76978 9 1.76978V0.769775Z"
                fill={Color.GRAY_400}
              />
            </svg>
            <div className={css(styles.chipSection)}>
              <FieldLabel label="Current link" />
              <div className={css(styles.linkChipWrapper, styles.existingLink)}>
                <BioentityChip
                  className={css(styles.ellipsizedText)}
                  id={props.existingLinkPolymerId}
                  isLink={false}
                  size={ChipSize.TABLE_CELL}
                />
              </div>

              <FieldLabel label="Updated link" className={css(styles.secondLabel)} />
              <div className={css(styles.linkChipWrapper, styles.newLink)}>
                <BioentityChip
                  className={css(styles.ellipsizedText)}
                  id={props.newLinkPolymerId}
                  isLink={false}
                  size={ChipSize.TABLE_CELL}
                />
              </div>
            </div>
            <svg className={css(styles.arrow)}>
              <path
                d="M0 3.26978L5 6.15652L5 0.383026L0 3.26978ZM0 78.7698H4V77.7698H0V78.7698ZM12.5 70.2698L12.5 11.2698H11.5L11.5 70.2698H12.5ZM12.5 11.2698C12.5 6.57536 8.69442 2.76978 4 2.76978V3.76978C8.14214 3.76978 11.5 7.12764 11.5 11.2698H12.5ZM4 78.7698C8.69442 78.7698 12.5 74.9642 12.5 70.2698H11.5C11.5 74.4119 8.14214 77.7698 4 77.7698V78.7698Z"
                fill={Color.GRAY_400}
              />
            </svg>
          </div>
        </>
      }
    >
      {searchParams != null && (
        <PolymerSearchView
          {...searchParams}
          navSection={Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE}
          selectedBioentityIds={props.selectedIds}
          setSelectedBioentityIds={props.setSelectedIds}
          rowSelectionMode={RowSelectionMode.MULTIPLE}
        />
      )}
    </PaneWithSidebar>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_14,
    marginBottom: '24px',
  },
  chipSection: {
    flex: 1,
    minWidth: '0px',
  },
  linkChipWrapper: {
    borderRadius: '4px',
    border: `1px solid ${Color.GRAY_300}`,
    padding: '4px',
  },
  existingLink: {
    backgroundColor: Color.ORANGE_100,
  },
  newLink: {
    backgroundColor: Color.TEAL_100,
  },
  diagram: {
    display: 'flex',
  },
  arrow: {
    width: '13px',
    marginTop: '32px',
  },
  secondLabel: {
    marginTop: '22px',
  },
  ellipsizedText: {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
});
